// src/components/DonationForm.js
import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useChatBot } from '../context/ChatBotContext';
import * as Yup from 'yup';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormHelperText,
  TextField,
  Button,
  Paper,
  Typography,
  Box,
  Snackbar,
  Alert,
  Chip
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DebugPanel from '../components/DebugPanel';
import UserHeader from '../components/UserHeader';
import { useSettings } from '../context/SettingsContext';


const DonationSchema = Yup.object().shape({
  donationType: Yup.string().required('Please select a donation amount'),
  customAmount: Yup.string().when('donationType', {
    is: 'other',
    then: () => Yup.string()
      .required('Please enter a custom amount')
      .matches(/^\d+$/, 'Please enter a valid number'),
    otherwise: () => Yup.string()
  })
});

const presetAmounts = [
  { label: 'Do not add a donation', value: '0' },
  { label: '£5', value: '5' },
  { label: '£15', value: '15' },
  { label: '£30', value: '30' },
  { label: '£50', value: '50' },
  { label: 'Other', value: 'other' }
];

const CHECKOUT_URL = '/api/create-checkout-session';

function DonationForm() {
  const { userData, updateField } = useUser();
  const { getPricingConfig, getSeasonConfig } = useSettings();
  const { setPageContent, clearPageContent } = useChatBot();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminAdd = location.state?.isAdminAdd || false;
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const pricing = getPricingConfig();

  useEffect(() => {
    const cutoffDateStr = pricing.CUTOFF_DATE ? new Date(pricing.CUTOFF_DATE).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'April';
    const content = `CCSC Donation Page - Add a Donation to Your Membership

Donation Options:
- No donation (£0)
- £5 donation
- £15 donation
- £30 donation
- £50 donation
- Custom amount (enter your own amount)

About Donations:
- Donations help support the club's running costs and improvements
- All donations are voluntary and greatly appreciated
- Donations are added to your membership fee at checkout

Payment:
- Secure payment processing via Stripe
- You'll be redirected to Stripe's secure checkout
- Card payments accepted (Visa, Mastercard, etc.)

Membership Cost Summary:
- Individual membership: £${pricing.INDIVIDUAL_EARLY} (early bird before ${cutoffDateStr}) / £${pricing.INDIVIDUAL_LATE} (after)
- Family membership: £${pricing.FAMILY_EARLY} (early bird) / £${pricing.FAMILY_LATE} (after)
- 1st Safety Officers receive ${pricing.FIRST_OFFICER_DISCOUNT}% discount on membership
- Early bird prices apply before ${cutoffDateStr}`;
    
    setPageContent(content);
    return () => clearPageContent();
  }, [setPageContent, clearPageContent, pricing]);

  const calculateMembershipCost = () => {
    const pricing = getPricingConfig();
    const currentDate = new Date();
    const cutoffDate = new Date(pricing.CUTOFF_DATE);
    const hasFamily = userData.familyMembers && userData.familyMembers.length > 0;
    const is1stSafetyOfficer = userData.dutyType === '1st Safety Officer';
    
    let baseCost;
    if (hasFamily) {
      baseCost = currentDate < cutoffDate ? pricing.FAMILY_EARLY : pricing.FAMILY_LATE;
    } else {
      baseCost = currentDate < cutoffDate ? pricing.INDIVIDUAL_EARLY : pricing.INDIVIDUAL_LATE;
    }
    
    const discountMultiplier = is1stSafetyOfficer ? (100 - pricing.FIRST_OFFICER_DISCOUNT) / 100 : 1;
    return baseCost * discountMultiplier;
  };

  const membershipCost = calculateMembershipCost();
  const showDiscountText = userData.dutyType === '1st Safety Officer';

  const getCurrentDonationType = (currentDonation) => {
    const presetValue = presetAmounts.find(preset => preset.value === currentDonation);
    return presetValue ? presetValue.value : 'other';
  };

  const initialDonationType = getCurrentDonationType(userData.donation);

  const handleAdminAddMember = async (values, setSubmitting) => {
    try {
      const adminToken = sessionStorage.getItem('adminToken');
      
      if (!adminToken) {
        throw new Error('Admin session expired. Please login again.');
      }
      
      const response = await fetch('/api/admin/add-member', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          memberData: {
            ...userData,
            email: userData.contactEmail,
            firstName: userData.firstName,
            lastName: userData.lastName,
            surname: userData.lastName,
          }
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to add member');
      }

      setSnackbar({
        open: true,
        message: 'Member added successfully! Redirecting to admin...',
        severity: 'success'
      });
      
      setTimeout(() => {
        navigate('/admin');
      }, 2000);

    } catch (error) {
      console.error('Admin add member error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error adding member. Please try again.',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitToCheckout = async (values, setSubmitting) => {
    try {
      const finalAmount = values.donationType === 'other' ? values.customAmount : values.donationType;
      updateField('donation', finalAmount);

      const response = await fetch(CHECKOUT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...userData,
          donation: finalAmount
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Network response was not ok');
      }

      const { url } = await response.json();
      
      setSnackbar({
        open: true,
        message: 'Taking you to the Stripe checkout',
        severity: 'success'
      });
      
      window.location.href = url;

    } catch (error) {
      console.error('Submission error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error submitting. Please try again.',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <UserHeader />
      <Paper 
        elevation={3} 
        sx={{ 
          maxWidth: 400, 
          mx: 'auto', 
          p: 4,
          mt: 8
        }}
      >
      {isAdminAdd && (
        <Chip
          icon={<PersonAddIcon />}
          label="Admin: Adding Member Manually"
          sx={{
            mb: 2,
            backgroundColor: '#0d2c54',
            color: '#fff',
            fontWeight: 600,
            '& .MuiChip-icon': { color: '#dbf14e' }
          }}
        />
      )}

      <Typography 
        variant="h5" 
        component="h2" 
        align="center" 
        gutterBottom 
        sx={{ mb: 2 }}
      >
        {isAdminAdd ? 'Complete Member Details' : 'Add a Donation?'}
      </Typography>

      <Typography 
        variant="body1" 
        align="center" 
        sx={{ mb: 4 }}
      >
        Your membership for {getSeasonConfig().REGISTRATION_YEAR} is £{membershipCost}
        {showDiscountText && ' (including 50% 1st Safety Officer discount)'}
        . Would you like to add a donation?
      </Typography>
      
      <Formik
        initialValues={{
          donationType: initialDonationType,
          customAmount: initialDonationType === 'other' ? userData.donation : ''
        }}
        validationSchema={DonationSchema}
        onSubmit={(values, { setSubmitting }) => {
          if (isAdminAdd) {
            handleAdminAddMember(values, setSubmitting);
          } else {
            handleSubmitToCheckout(values, setSubmitting);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
          <Form>
            <FormControl component="fieldset" fullWidth error={touched.donationType && !!errors.donationType}>
              <RadioGroup
                name="donationType"
                value={values.donationType}
                onChange={handleChange}
              >
                {presetAmounts.map((amount) => (
                  <FormControlLabel
                    key={amount.value}
                    value={amount.value}
                    control={<Radio />}
                    label={amount.label}
                    sx={{ mb: 1 }}
                  />
                ))}
              </RadioGroup>
              {touched.donationType && errors.donationType && (
                <FormHelperText>{errors.donationType}</FormHelperText>
              )}
            </FormControl>

            {values.donationType === 'other' && (
              <Box sx={{ mt: 2, mb: 3 }}>
                <TextField
                  fullWidth
                  id="customAmount"
                  name="customAmount"
                  label="Custom Amount"
                  InputProps={{
                    startAdornment: '£',
                  }}
                  value={values.customAmount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.customAmount && !!errors.customAmount}
                  helperText={touched.customAmount && errors.customAmount}
                  variant="outlined"
                />
              </Box>
            )}

            <Box sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isSubmitting}
                startIcon={isAdminAdd ? <PersonAddIcon /> : null}
                sx={{ 
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  backgroundColor: isAdminAdd ? '#0d2c54' : undefined,
                  color: isAdminAdd ? '#ffffff' : undefined,
                  '&:hover': isAdminAdd ? { backgroundColor: '#1a4778' } : undefined
                }}
              >
                {isSubmitting ? 'Processing...' : (isAdminAdd ? 'Add Member (No Payment)' : 'Continue to Payment')}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
                      {/* Debug Panel */}
                      {process.env.NODE_ENV === 'development' && (
                  <DebugPanel contextData={userData} />
                )}
    </Paper>
    </>
  );
}

export default DonationForm;